import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Users, Music, ChevronDown, ChevronUp, RefreshCw, ShieldOff, ShieldCheck, FileJson, Server } from 'lucide-react';
import { FleetServersPanel } from './FleetServersPanel';
import { PerfIndexPanel } from './PerfIndexPanel';

const API = process.env.REACT_APP_BACKEND_URL;

// v2.13.204 — Shared helper used by BOTH the Global export button and
// the per-Pipeline export buttons. Fetches the yellow-stuck-report from
// the SAME existing endpoint (no new endpoint, no extra polling), then
// optionally filters ``yellow_stations`` + ``stuck_stations`` by the
// station-id set that belongs to a given Pipeline (SHAZAM / ICY /
// OTHER). All filtering is client-side; the backend never receives a
// pipeline query parameter.
async function exportYellowStuckSnapshot({ token, pipelineKey, pipelineStationIds, pipelineLabel }) {
  const res = await fetch(`${API}/api/admin/diagnostic/yellow-stuck-report`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  let data = await res.json();
  if (pipelineKey && Array.isArray(pipelineStationIds)) {
    const idSet = new Set(pipelineStationIds.map(String));
    const inSet = (s) => s && idSet.has(String(s.station_id || s.id || s._id || ''));
    const filteredYellow = Array.isArray(data.yellow_stations)
      ? data.yellow_stations.filter(inSet) : [];
    const filteredStuck = Array.isArray(data.stuck_stations)
      ? data.stuck_stations.filter(inSet) : [];
    data = {
      _filtered_pipeline: pipelineKey,
      _filtered_pipeline_label: pipelineLabel,
      _filter_station_count: idSet.size,
      generated_at_utc: data.generated_at_utc,
      generated_at_greek: data.generated_at_greek,
      environment_url: data.environment_url,
      totals: {
        yellow: filteredYellow.length,
        stuck: filteredStuck.length,
      },
      yellow_stations: filteredYellow,
      stuck_stations: filteredStuck,
    };
  }
  const text = JSON.stringify(data, null, 2);
  try {
    await navigator.clipboard.writeText(text);
    return {
      copied: true,
      yellowCount: data?.totals?.yellow || 0,
      stuckCount: data?.totals?.stuck || 0,
    };
  } catch {
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const suffix = pipelineKey ? `-${pipelineKey.toLowerCase()}` : '';
    a.href = url;
    a.download = `yellow-stuck-report${suffix}-${ts}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return {
      copied: false,
      yellowCount: data?.totals?.yellow || 0,
      stuckCount: data?.totals?.stuck || 0,
    };
  }
}

const COLOR_DEF = {
  green: 'Πράσινο: σιωπή ≤10΄ και ≥5 διαφορετικά τραγούδια στα 30΄',
  yellow: 'Κίτρινο: σιωπή ≤10΄ αλλά <5 διαφορετικά τραγούδια στα 30΄',
  stuck: 'Stuck: σιωπή 10–20΄ μέσα στο πρόγραμμα',
  red: 'Κόκκινο: σιωπή >20΄ μέσα στο πρόγραμμα',
  off_zone: 'Γκρι: εκτός προγράμματος καταγραφής',
  inactive: 'Γκρι: ανενεργός σταθμός (δεν παρακολουθείται)',
};

const lastSongLine = (s) => {
  if (!s.last_song) return 'Τελευταίο τραγούδι: κανένα σήμερα';
  const t = new Date(s.last_song.endsWith('Z') || s.last_song.includes('+') ? s.last_song : `${s.last_song}Z`);
  const hm = t.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Athens' });
  return `Τελευταίο τραγούδι: ${hm} (πριν ${Math.round((s.last_song_age_seconds || 0) / 60)}΄)`;
};

const HealthDot = ({ status }) => {
  const colors = {
    green: 'bg-emerald-500 shadow-emerald-500/50',
    yellow: 'bg-amber-400 shadow-amber-400/50',
    red: 'bg-red-500 shadow-red-500/50 animate-pulse',
    stuck: 'bg-cyan-400 shadow-cyan-400/50',
    off_zone: 'bg-slate-400 shadow-slate-400/50',
    inactive: 'bg-gray-400 dark:bg-gray-500',
  };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full shadow-sm ${colors[status] || 'bg-gray-400'}`} />;
};

// v28 (May 2026): per-station listener-tracking health indicator
// v28.1 (May 2026): added "from_plays" — neutral blue for stations
// whose listener data comes from per-song captures (Live Audience /
// Stream Analytics modes), not from the standalone tracker.
const ListenerDot = ({ status }) => {
  const colors = {
    fresh:       'bg-emerald-500 shadow-emerald-500/50',
    ok:          'bg-amber-400 shadow-amber-400/50',
    warning:     'bg-orange-500 shadow-orange-500/50',
    stale:       'bg-red-500 shadow-red-500/50 animate-pulse',
    from_plays:  'bg-sky-400 shadow-sky-400/50',
    auth:        'bg-fuchsia-500 shadow-fuchsia-500/50',
    no_endpoint: 'bg-gray-400 dark:bg-gray-500',
    inactive:    'bg-gray-300 dark:bg-gray-600',
  };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full shadow-sm ${colors[status] || 'bg-gray-400'}`} />;
};

const StationHealthDashboard = ({ token, onStationClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  // Per-Pipeline expansion state (independent from Global expansion).
  // Keys: 'SHAZAM' | 'ICY' | 'OTHER'. All rows are permanently VISIBLE
  // (Section 6/25); this state only controls the station-grid
  // expansion inside each row, matching the Global row's own toggle.
  const [serversExpanded, setServersExpanded] = useState(false);
  const [pipelineExpanded, setPipelineExpanded] = useState({
    SHAZAM: false, ICY: false, OTHER: false,
  });
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/diagnostic/health-summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setLastUpdate(new Date());
      }
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await fetchHealth();
    })();
    const interval = setInterval(fetchHealth, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [fetchHealth]);

  if (loading || !data) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 animate-pulse" data-testid="health-dashboard-loading">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48" />
      </div>
    );
  }

  const { totals, stations } = data;

  const cellStyle = (health) => {
    switch (health) {
      case 'green':
        return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400';
      case 'yellow':
        return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400';
      case 'red':
        return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400';
      case 'stuck':
        return 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400';
      case 'off_zone':
        return 'bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400';
      case 'inactive':
      default:
        return 'bg-gray-100 dark:bg-gray-800/40 text-gray-400 dark:text-gray-500';
    }
  };

  // Helper: tooltip text for off-zone stations
  const offZoneLabel = (s) => {
    const ws = s.schedule_start_time || '06:00';
    const we = s.schedule_end_time || '21:00';
    return `Εκτός ζώνης καταγραφής (${ws}–${we})`;
  };

  // v28: Helper for listener-tracking tooltip + cell style
  const listenerCellStyle = (lh) => {
    switch (lh) {
      case 'fresh':
        return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400';
      case 'ok':
        return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400';
      case 'warning':
        return 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400';
      case 'stale':
        return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400';
      case 'from_plays':
        return 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400';
      case 'auth':
        return 'bg-fuchsia-50 dark:bg-fuchsia-900/20 text-fuchsia-700 dark:text-fuchsia-400';
      case 'no_endpoint':
        return 'bg-gray-100 dark:bg-gray-800/40 text-gray-500 dark:text-gray-400';
      case 'inactive':
      default:
        return 'bg-gray-100 dark:bg-gray-800/40 text-gray-400 dark:text-gray-500';
    }
  };

  const listenerTooltip = (s) => {
    const lh = s.listener_health;
    const ago = s.listener_last_sample_seconds_ago;
    const peak = s.listener_today_peak || 0;
    const avg = s.listener_today_avg || 0;
    const fmt = (sec) => {
      if (sec == null) return 'never';
      const m = Math.floor(sec / 60);
      const h = Math.floor(m / 60);
      if (h > 0) return `${h}h ${m % 60}m`;
      if (m > 0) return `${m}m ${sec % 60}s`;
      return `${sec}s`;
    };
    const status =
      lh === 'fresh'       ? 'Φρέσκο (≤ 60s, 1-2 polls)' :
      lh === 'ok'          ? 'OK (≤ 3 λ., μέσα στο τραγούδι)' :
      lh === 'warning'     ? 'Προσοχή (≤ 10 λ., 2-3 τραγούδια χαμένα)' :
      lh === 'stale'       ? 'Νεκρό endpoint (>10 λ., 3+ τραγούδια χαμένα)' :
      lh === 'from_plays'  ? 'Από τα τραγούδια (όχι standalone)' :
      lh === 'auth'        ? 'AUTH 401 — το Live24 απαιτεί πιστοποίηση' :
      lh === 'no_endpoint' ? 'Χωρίς listener endpoint' :
      lh === 'inactive'    ? 'Ανενεργός σταθμός' :
      lh;
    if (lh === 'no_endpoint' || lh === 'inactive') {
      return `${s.name}\nListener tracking: ${status}\nΚλικ: μετάβαση στον σταθμό`;
    }
    if (lh === 'auth') {
      return `${s.name}\nListener tracking: ${status}\nΤο stats endpoint απαντά 401/403· νέα δοκιμή κάθε 10 λ.\nΚλικ: μετάβαση στον σταθμό`;
    }
    if (lh === 'from_plays') {
      return `${s.name}\nListener tracking: ${status}\nΤα listeners καταγράφονται κατά την αναπαραγωγή τραγουδιών.\nToday peak: ${peak} | Avg: ${avg}\nΚλικ: μετάβαση στον σταθμό`;
    }
    return `${s.name}\nListener tracking: ${status}\nLast sample: ${fmt(ago)} ago\nToday peak: ${peak} | Avg: ${avg}\nΚλικ: μετάβαση στον σταθμό`;
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-testid="station-health-dashboard">
      {/* Summary Bar */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(!expanded);
          }
        }}
        className="w-full flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
        data-testid="health-dashboard-toggle"
        aria-expanded={expanded}
      >
        {/* v2.13.64 — mobile: flex-wrap so every chip (health dots,
            listener tracking, songs/hr, listeners, AudD) is visible
            even on a 360px-wide phone. Previously the listener
            tracking and music/listeners chips were `hidden md:flex` /
            `hidden sm:flex`, leaving the operator blind to the
            polling pipeline on mobile. */}
        <div className="flex items-center gap-x-3 gap-y-1.5 sm:gap-4 text-sm flex-wrap min-w-0">
          <div className="flex items-center gap-1.5 font-medium text-gray-700 dark:text-gray-300">
            <Activity className="w-4 h-4 text-blue-500" />
            <span>Pipeline Health</span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400" title={COLOR_DEF.green} data-testid="health-green-count">
              <HealthDot status="green" /> {totals.green}
            </span>
            <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400" title={COLOR_DEF.yellow} data-testid="health-yellow-count">
              <HealthDot status="yellow" /> {totals.yellow}
            </span>
            <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400" title={COLOR_DEF.red} data-testid="health-red-count">
              <HealthDot status="red" /> {totals.red}
            </span>
            {totals.stuck > 0 && (
              <span
                className="flex items-center gap-1.5 text-cyan-600 dark:text-cyan-400"
                title={COLOR_DEF.stuck}
                data-testid="health-stuck-count"
              >
                <HealthDot status="stuck" /> {totals.stuck}
              </span>
            )}
            {totals.off_zone > 0 && (
              <span
                className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400"
                title={COLOR_DEF.off_zone}
                data-testid="health-off-zone-count"
              >
                <HealthDot status="off_zone" /> {totals.off_zone}
              </span>
            )}
            {totals.inactive > 0 && (
              <span className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500" title={COLOR_DEF.inactive} data-testid="health-inactive-count">
                <HealthDot status="inactive" /> {totals.inactive}
              </span>
            )}
          </div>

          {/* v28: Listener-tracking health summary
              v2.9.165: tighter thresholds → 60s / 3min / 10min / >10min
              (1-2 polls / 1 song / 2-3 songs / dead endpoint)
              v2.13.64: visible on mobile too (was `hidden md:flex`) */}
          <span className="text-gray-500 dark:text-gray-400">|</span>
          <div className="flex items-center gap-2 text-xs flex-wrap" title="Listener-tracking health (per station endpoint freshness)">
            <Users className="w-3.5 h-3.5 text-blue-500" />
            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400" title="≤ 60s (1-2 polls — μέσα στο τρέχον τραγούδι)">
              <ListenerDot status="fresh" /> {totals.listener_fresh || 0}
            </span>
            <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400" title="≤ 3 λ. (network blip — ακόμα μέσα στο ίδιο τραγούδι)">
              <ListenerDot status="ok" /> {totals.listener_ok || 0}
            </span>
            {(totals.listener_warning || 0) > 0 && (
              <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400" title="≤ 10 λ. (χάθηκαν 2-3 τραγούδια — slow endpoint)">
                <ListenerDot status="warning" /> {totals.listener_warning}
              </span>
            )}
            {(totals.listener_from_plays || 0) > 0 && (
              <span className="flex items-center gap-1 text-sky-600 dark:text-sky-400" title="Από τα τραγούδια (Live Audience / Stream Analytics)">
                <ListenerDot status="from_plays" /> {totals.listener_from_plays}
              </span>
            )}
            {(totals.listener_auth || 0) > 0 && (
              <span className="flex items-center gap-1 text-fuchsia-600 dark:text-fuchsia-400" title="AUTH 401 — το Live24 απαιτεί πιστοποίηση (νέα δοκιμή κάθε 10 λ.)" data-testid="listener-auth-total">
                <ListenerDot status="auth" /> {totals.listener_auth}
              </span>
            )}
            {(totals.listener_stale || 0) > 0 && (
              <span className="flex items-center gap-1 text-red-600 dark:text-red-400" title="> 10 λ. (νεκρό endpoint — χρειάζεται έλεγχος URL/auth)">
                <ListenerDot status="stale" /> {totals.listener_stale}
              </span>
            )}
            {(totals.listener_no_endpoint || 0) > 0 && (
              <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400" title="Χωρίς listener endpoint (όχι σφάλμα)">
                <ListenerDot status="no_endpoint" /> {totals.listener_no_endpoint}
              </span>
            )}
          </div>

          <span className="text-gray-500 dark:text-gray-400">|</span>

          <span
            className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400"
            data-testid="health-songs-counts"
            title={`Αποθηκευμένα τραγούδια στη βάση (όλοι οι servers)\nΤελευταία 60΄: ${totals.total_songs_1h}\nΠροηγούμενη πλήρης ώρα: ${totals.songs_prev_full_hour ?? '—'}\nΤρέχουσα ώρα μέχρι τώρα: ${totals.songs_current_hour_so_far ?? '—'}`}
          >
            <Music className="w-3.5 h-3.5" />
            <span data-testid="health-songs-last-60m">{totals.total_songs_1h}</span><span className="text-xs">τελ. 60΄</span>
            {totals.songs_prev_full_hour_start && (
              <>
                <span className="text-xs opacity-60">·</span>
                <span data-testid="health-songs-prev-full-hour">{totals.songs_prev_full_hour}</span>
                <span className="text-xs">
                  ώρα {new Date(totals.songs_prev_full_hour_start).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit', hour12: false })}
                </span>
                <span className="text-xs opacity-60">·</span>
                <span data-testid="health-songs-current-hour">{totals.songs_current_hour_so_far}</span>
                <span className="text-xs">τρέχουσα</span>
              </>
            )}
          </span>

          {stations.some((s) => s.needs_review) && (
            <span
              className="flex items-center gap-1.5 text-red-600 dark:text-red-400 font-medium"
              data-testid="health-needs-review"
              title={stations.filter((s) => s.needs_review).map((s) =>
                `${s.name}: ${s.needs_review.kind === 'permanent' ? 'η πηγή αρνείται / δεν υπάρχει' : 'η πηγή δεν απαντά'} (${s.needs_review.reason}) · ${s.needs_review.idle_minutes}΄ · ${s.needs_review.attempts} προσπάθειες`
              ).join('\n')}
            >
              Χρειάζονται έλεγχο: {stations.filter((s) => s.needs_review).length}
            </span>
          )}

          <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
            <Users className="w-3.5 h-3.5" /> {(totals.total_listeners || 0).toLocaleString()}
          </span>

          <span className="text-gray-500 dark:text-gray-400">|</span>

          <span className={`flex items-center gap-1.5 ${totals.audd_enabled ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`} data-testid="audd-usage-summary" title={totals.audd_enabled ? 'AudD ενεργοποιημένο (paid) — κλήσεις σήμερα / ημερήσιο όριο' : 'AudD απενεργοποιημένο. Όλες οι αναγνωρίσεις γίνονται με Shazam (δωρεάν).'}>
            {totals.audd_enabled ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
            {totals.audd_enabled ? (
              <>AudD: {totals.audd_calls_today ?? 0}/{totals.audd_daily_limit ?? 0}</>
            ) : (
              <>AudD: OFF <span className="text-[10px] text-gray-400 ml-0.5">(Shazam-only)</span></>
            )}
          </span>
        </div>

        <div className="flex items-center gap-2 text-gray-400 flex-shrink-0">
          {lastUpdate && (
            <span className="text-xs hidden sm:inline">
              {lastUpdate.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {/* v2.10.0 — Yellow + Stuck Stations Diagnostic Report.
              Generates a complete JSON snapshot the operator can copy
              and forward to the agent for deep diagnosis. */}
          <button
            type="button"
            data-testid="export-yellow-stuck-report-btn"
            title="Εξαγωγή Αναφοράς Yellow + Stuck σταθμών (JSON) — για βαθιά διάγνωση"
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const res = await fetch(`${API}/api/admin/diagnostic/yellow-stuck-report`, {
                  headers: { 'Authorization': `Bearer ${token}` },
                });
                if (!res.ok) {
                  alert(`Export απέτυχε (HTTP ${res.status})`);
                  return;
                }
                const data = await res.json();
                const text = JSON.stringify(data, null, 2);
                // Try clipboard first; fall back to download
                try {
                  await navigator.clipboard.writeText(text);
                  alert(
                    `✅ Αναφορά Yellow + Stuck αντιγράφηκε στο clipboard\n\n` +
                    `Σύνολα: ${data?.totals?.yellow || 0} yellow, ` +
                    `${data?.totals?.stuck || 0} stuck.\n\n` +
                    `Επικόλληση: Ctrl+V (ή Cmd+V) όπου χρειάζεται.`
                  );
                } catch {
                  const blob = new Blob([text], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                  a.href = url;
                  a.download = `yellow-stuck-report-${ts}.json`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }
              } catch (err) {
                alert(`Export error: ${err?.message || err}`);
              }
            }}
            className="p-1 rounded hover:bg-blue-500/10 hover:text-blue-500 transition-colors"
          >
            <FileJson className="w-3.5 h-3.5" />
          </button>
          <RefreshCw
            className="w-3.5 h-3.5 cursor-pointer hover:text-blue-500 transition-colors"
            onClick={(e) => { e.stopPropagation(); fetchHealth(); }}
          />
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>

      {/* Expanded Grid - Alphabetical order */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2 space-y-3" data-testid="health-dashboard-grid">
          {/* ── Row 1: Music recording health ─────────────────────────── */}
          <div>
            <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <Music className="w-3 h-3" /> Music Recording
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
              {stations.map((s) => (
                <button
                  key={`m-${s.id}`}
                  onClick={() => onStationClick?.(s.id)}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-xs transition-colors text-left w-full hover:ring-1 hover:ring-purple-400/50 cursor-pointer ${cellStyle(s.health)}`}
                  title={
                    s.health === 'inactive'
                      ? `${s.name}\nΑνενεργός\nΚλικ: μετάβαση στον σταθμό`
                      : s.health === 'stuck'
                      ? `${s.name}\n${COLOR_DEF.stuck}\n${lastSongLine(s)}\nSongs/hr: ${s.songs_1h} | Raw: ${s.raw_1h} | Listeners: ${s.listeners}\nSources: ${s.sources.join(', ') || 'None'}\nΚλικ: μετάβαση στον σταθμό`
                      : s.health === 'off_zone'
                      ? `${s.name}\n${offZoneLabel(s)}\nListeners: ${s.listeners}\nΚλικ: μετάβαση στον σταθμό`
                      : `${s.name}\n${COLOR_DEF[s.health] || ''}\n${lastSongLine(s)} · ${s.songs_30m ?? 0} τραγούδια στα 30΄\nSongs/hr: ${s.songs_1h} | Raw: ${s.raw_1h} | Listeners: ${s.listeners}\nSources: ${s.sources.join(', ') || 'None'}\nΚλικ: μετάβαση στον σταθμό`
                  }
                  data-testid={`health-station-${s.id}`}
                >
                  <HealthDot status={s.health} />
                  <span className="truncate font-medium">{s.name}</span>
                  {s.health === 'stuck' && (
                    <span className="ml-auto text-[10px] uppercase tracking-wide opacity-70 whitespace-nowrap" data-testid={`health-stuck-badge-${s.id}`}>
                      stuck
                    </span>
                  )}
                  {s.health === 'off_zone' && (
                    <span className="ml-auto text-[10px] uppercase tracking-wide opacity-70 whitespace-nowrap" data-testid={`health-off-zone-badge-${s.id}`}>
                      off-zone
                    </span>
                  )}
                  {s.health !== 'inactive' && s.health !== 'stuck' && s.health !== 'off_zone' && (
                    <span className="ml-auto tabular-nums opacity-70 whitespace-nowrap">{s.songs_1h}</span>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-2 text-[10px] text-gray-400 dark:text-gray-500 flex gap-3 flex-wrap">
              <span data-testid="health-legend-rule">Μέσα στο ωράριο:</span>
              <span>🟢 5+ τραγούδια στα 30΄</span>
              <span>🟡 λιγότερα από 5 στα 30΄</span>
              <span>🩵 Stuck: σιωπή 10–20΄</span>
              <span>🔴 σιωπή πάνω από 20΄</span>
              <span>⬜ Off-zone (εκτός ωραρίου)</span>
              <span>⚪ Ανενεργός</span>
            </div>
          </div>

          {/* ── Row 2 (v28, May 2026): Listener-tracking health ───────── */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <Users className="w-3 h-3" /> Listener Tracking
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
              {stations.map((s) => {
                const lh = s.listener_health || 'inactive';
                return (
                  <button
                    key={`l-${s.id}`}
                    onClick={() => onStationClick?.(s.id)}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-xs transition-colors text-left w-full hover:ring-1 hover:ring-purple-400/50 cursor-pointer ${listenerCellStyle(lh)}`}
                    title={listenerTooltip(s)}
                    data-testid={`listener-health-station-${s.id}`}
                  >
                    <ListenerDot status={lh} />
                    <span className="truncate font-medium">{s.name}</span>
                    {/* Right-side detail per state */}
                    {lh === 'no_endpoint' && (
                      <span className="ml-auto text-[10px] uppercase tracking-wide opacity-70 whitespace-nowrap">
                        no API
                      </span>
                    )}
                    {lh === 'from_plays' && (
                      <span className="ml-auto text-[10px] uppercase tracking-wide opacity-70 whitespace-nowrap">
                        plays
                      </span>
                    )}
                    {lh === 'auth' && (
                      <span className="ml-auto text-[10px] uppercase tracking-wide opacity-70 whitespace-nowrap" data-testid={`listener-auth-badge-${s.id}`}>
                        auth 401
                      </span>
                    )}
                    {lh === 'stale' && (
                      <span className="ml-auto text-[10px] uppercase tracking-wide opacity-70 whitespace-nowrap">
                        stale
                      </span>
                    )}
                    {(lh === 'fresh' || lh === 'ok' || lh === 'warning') && (
                      <span className="ml-auto tabular-nums opacity-70 whitespace-nowrap">
                        {s.listener_today_peak || 0}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 text-[10px] text-gray-400 dark:text-gray-500 flex gap-3 flex-wrap">
              <span>🟢 ≤ 60s (1-2 polls)</span>
              <span>🟡 ≤ 3 λ. (μέσα στο τραγούδι)</span>
              <span>🟠 ≤ 10 λ. (2-3 τραγούδια χαμένα)</span>
              <span>🔵 Από τα τραγούδια (Live Audience)</span>
              <span>🟣 AUTH 401 (το Live24 απαιτεί πιστοποίηση)</span>
              <span>🔴 &gt; 10 λ. (νεκρό endpoint)</span>
              <span>⚫ Χωρίς listener endpoint</span>
            </div>
          </div>
        </div>
      )}
      {/* Servers — own collapsible bar, separate from the station pipeline (2026-11-01).
          Mounted only when open, so its 30s polling stops while collapsed. */}
      <div data-testid="servers-bar">
        <div
          role="button"
          tabIndex={0}
          onClick={() => setServersExpanded((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setServersExpanded((v) => !v);
            }
          }}
          className="w-full flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer border-t border-gray-200 dark:border-gray-700"
          data-testid="servers-bar-toggle"
          aria-expanded={serversExpanded}
        >
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
            <Server className="w-4 h-4 text-blue-500" />
            <span>SERVERS</span>
            <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 ml-1">
              λειτουργία servers · κενά σήμερα · κοινά τραγούδια
            </span>
          </div>
          {serversExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
        {serversExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2" data-testid="servers-bar-content">
            <FleetServersPanel token={token} />
            <PerfIndexPanel token={token} />
          </div>
        )}
      </div>
      {/* ═════════════════════════════════════════════════════════════
          v2.13.204 — PIPELINE BREAKDOWN (SHAZAM / ICY / OTHER)
          Permanently visible partition of the Global Pipeline above.
          Same summary layout, same dots, same colours, same expansion
          behaviour. Every station is classified DYNAMICALLY from its
          existing stream_stations configuration fields — no static ID
          mapping, no code change required when a station is added,
          removed or reconfigured.
          Global Pipeline (row above) is byte-identical to prior
          versions; this section is a read-only breakdown that operates
          on the same rows already fetched by the endpoint.
      ═════════════════════════════════════════════════════════════ */}
      {data.by_pipeline && (
        <div className="border-t border-gray-200 dark:border-gray-700" data-testid="pipeline-breakdown-container">
          {['SHAZAM', 'ICY', 'OTHER'].map((pipelineKey) => {
            const bucket = data.by_pipeline[pipelineKey] || {};
            const rowExpanded = pipelineExpanded[pipelineKey];
            const label = pipelineKey === 'SHAZAM'
              ? 'SHAZAM'
              : pipelineKey === 'ICY'
              ? 'ICY / STREAM METADATA'
              : 'OTHER METADATA';
            // Filtered station list, reusing the existing station rows
            // stamped with .pipeline by the backend classifier.
            const filteredStations = (stations || []).filter(
              (s) => s.pipeline === pipelineKey,
            );
            const musicStations = filteredStations;
            const listenerStations = filteredStations;
            const toggleRow = () => setPipelineExpanded((p) => ({
              ...p, [pipelineKey]: !p[pipelineKey],
            }));
            return (
              <div key={pipelineKey} data-testid={`pipeline-row-${pipelineKey}`}>
                {/* Summary row — same style/rhythm as Global row */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={toggleRow}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleRow();
                    }
                  }}
                  className="w-full flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer border-t border-gray-200 dark:border-gray-700"
                  data-testid={`pipeline-row-toggle-${pipelineKey}`}
                  aria-expanded={rowExpanded}
                >
                  <div className="flex items-center gap-x-3 gap-y-1.5 sm:gap-4 text-sm flex-wrap min-w-0">
                    <div className="flex items-center gap-1.5 font-medium text-gray-700 dark:text-gray-300">
                      <Activity className="w-4 h-4 text-blue-500" />
                      <span>{label}</span>
                      <span
                        className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 ml-1"
                        data-testid={`pipeline-active-count-${pipelineKey}`}
                        title="Ενεργοί σταθμοί σε αυτό το Pipeline (dynamic από station config)"
                      >
                        {bucket.active || 0} active
                      </span>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                        <HealthDot status="green" /> {bucket.green || 0}
                      </span>
                      <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                        <HealthDot status="yellow" /> {bucket.yellow || 0}
                      </span>
                      <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                        <HealthDot status="red" /> {bucket.red || 0}
                      </span>
                      {(bucket.stuck || 0) > 0 && (
                        <span className="flex items-center gap-1.5 text-cyan-600 dark:text-cyan-400">
                          <HealthDot status="stuck" /> {bucket.stuck}
                        </span>
                      )}
                      {(bucket.off_zone || 0) > 0 && (
                        <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                          <HealthDot status="off_zone" /> {bucket.off_zone}
                        </span>
                      )}
                      {(bucket.inactive || 0) > 0 && (
                        <span className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                          <HealthDot status="inactive" /> {bucket.inactive}
                        </span>
                      )}
                    </div>

                    <span className="text-gray-500 dark:text-gray-400">|</span>

                    <div className="flex items-center gap-2 text-xs flex-wrap" title="Listener-tracking health (per-Pipeline partition)">
                      <Users className="w-3.5 h-3.5 text-blue-500" />
                      <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                        <ListenerDot status="fresh" /> {bucket.listener_fresh || 0}
                      </span>
                      <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                        <ListenerDot status="ok" /> {bucket.listener_ok || 0}
                      </span>
                      {(bucket.listener_warning || 0) > 0 && (
                        <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                          <ListenerDot status="warning" /> {bucket.listener_warning}
                        </span>
                      )}
                      {(bucket.listener_from_plays || 0) > 0 && (
                        <span className="flex items-center gap-1 text-sky-600 dark:text-sky-400">
                          <ListenerDot status="from_plays" /> {bucket.listener_from_plays}
                        </span>
                      )}
                      {(bucket.listener_auth || 0) > 0 && (
                        <span className="flex items-center gap-1 text-fuchsia-600 dark:text-fuchsia-400">
                          <ListenerDot status="auth" /> {bucket.listener_auth}
                        </span>
                      )}
                      {(bucket.listener_stale || 0) > 0 && (
                        <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                          <ListenerDot status="stale" /> {bucket.listener_stale}
                        </span>
                      )}
                      {(bucket.listener_no_endpoint || 0) > 0 && (
                        <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                          <ListenerDot status="no_endpoint" /> {bucket.listener_no_endpoint}
                        </span>
                      )}
                    </div>

                    <span className="text-gray-500 dark:text-gray-400">|</span>

                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400" data-testid={`pipeline-songs-1h-${pipelineKey}`}>
                      <Music className="w-3.5 h-3.5" /> {bucket.total_songs_1h || 0} <span className="text-xs">τελ. 60΄</span>
                    </span>

                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400" data-testid={`pipeline-listeners-${pipelineKey}`}>
                      <Users className="w-3.5 h-3.5" /> {(bucket.total_listeners || 0).toLocaleString()}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-gray-400 flex-shrink-0">
                    <button
                      type="button"
                      data-testid={`pipeline-export-btn-${pipelineKey}`}
                      title={`Εξαγωγή Yellow + Stuck σταθμών ΜΟΝΟ για ${label} (JSON)`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const r = await exportYellowStuckSnapshot({
                            token,
                            pipelineKey,
                            pipelineStationIds: bucket.station_ids || [],
                            pipelineLabel: label,
                          });
                          if (r.copied) {
                            alert(
                              `✅ Αναφορά Yellow + Stuck — ${label} αντιγράφηκε στο clipboard\n\n` +
                              `Σύνολα: ${r.yellowCount} yellow, ${r.stuckCount} stuck.\n` +
                              `Φιλτραρίστηκε από ${(bucket.station_ids || []).length} σταθμούς αυτής της Pipeline.\n\n` +
                              `Επικόλληση: Ctrl+V (ή Cmd+V) όπου χρειάζεται.`
                            );
                          }
                        } catch (err) {
                          alert(`Export error: ${err?.message || err}`);
                        }
                      }}
                      className="p-1 rounded hover:bg-blue-500/10 hover:text-blue-500 transition-colors"
                    >
                      <FileJson className="w-3.5 h-3.5" />
                    </button>
                    {rowExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>

                {/* Expansion — same station cards + listener cards
                    as the Global row, filtered to this Pipeline only.
                    No fetch, no runtime side effect. */}
                {rowExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2 space-y-3 bg-gray-50/50 dark:bg-gray-800/30" data-testid={`pipeline-grid-${pipelineKey}`}>
                    {musicStations.length === 0 ? (
                      <div className="text-xs text-gray-500 dark:text-gray-400 italic py-2">
                        Κανένας σταθμός δεν έχει ταξινομηθεί σε αυτό το Pipeline.
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                            <Music className="w-3 h-3" /> Music Recording — {label}
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                            {musicStations.map((s) => (
                              <button
                                key={`p-${pipelineKey}-m-${s.id}`}
                                onClick={() => onStationClick?.(s.id)}
                                className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-xs transition-colors text-left w-full hover:ring-1 hover:ring-purple-400/50 cursor-pointer ${cellStyle(s.health)}`}
                                title={
                                  s.health === 'inactive'
                                    ? `${s.name}\nΑνενεργός\nPipeline: ${pipelineKey}\nΚλικ: μετάβαση στον σταθμό`
                                    : `${s.name}\nSongs/hr: ${s.songs_1h} | Raw: ${s.raw_1h} | Listeners: ${s.listeners}\nPipeline: ${pipelineKey}\nSources: ${(s.sources || []).join(', ') || 'None'}\nΚλικ: μετάβαση στον σταθμό`
                                }
                                data-testid={`pipeline-${pipelineKey}-station-${s.id}`}
                              >
                                <HealthDot status={s.health} />
                                <span className="truncate font-medium">{s.name}</span>
                                {s.health !== 'inactive' && s.health !== 'stuck' && s.health !== 'off_zone' && (
                                  <span className="ml-auto tabular-nums opacity-70 whitespace-nowrap">{s.songs_1h}</span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                          <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                            <Users className="w-3 h-3" /> Listener Tracking — {label}
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                            {listenerStations.map((s) => {
                              const lh = s.listener_health || 'inactive';
                              return (
                                <button
                                  key={`p-${pipelineKey}-l-${s.id}`}
                                  onClick={() => onStationClick?.(s.id)}
                                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-xs transition-colors text-left w-full hover:ring-1 hover:ring-purple-400/50 cursor-pointer ${listenerCellStyle(lh)}`}
                                  title={listenerTooltip(s)}
                                  data-testid={`pipeline-${pipelineKey}-listener-${s.id}`}
                                >
                                  <ListenerDot status={lh} />
                                  <span className="truncate font-medium">{s.name}</span>
                                  {(lh === 'fresh' || lh === 'ok' || lh === 'warning') && (
                                    <span className="ml-auto tabular-nums opacity-70 whitespace-nowrap">
                                      {s.listener_today_peak || 0}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {/* Optional UNCLASSIFIED surface — only rendered when the
              backend actually observed such stations. Section 22
              prohibits silently hiding them. */}
          {data.by_pipeline.UNCLASSIFIED && (data.by_pipeline.UNCLASSIFIED.active || 0) > 0 && (
            <div className="border-t border-amber-300/60 dark:border-amber-700/50 bg-amber-50/60 dark:bg-amber-900/10 px-3 sm:px-4 py-2 text-xs text-amber-700 dark:text-amber-400" data-testid="pipeline-unclassified-warning">
              ⚠️ {data.by_pipeline.UNCLASSIFIED.active} σταθμοί δεν ταξινομήθηκαν σε κάποιο Pipeline — ελλιπής configuration (καμία εκ των fingerprint_provider / fingerprint_unlimited / stream_url / metadata_api_url / website_url / stats_url / onlineradiobox_id / mytuner_id). Report the gap.
            </div>
          )}
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════
          Phase 2b (2026-02-26) — Per-Egress Distribution Bars.
          Two additive rows — one per configured egress identity
          (direct + hetzner1) — showing exactly which stations each
          IP is serving right now. Uses the SAME summary shape as the
          Pipeline breakdown above so the operator can spot at a
          glance:
            • load balance between IPs
            • sticky-routing effectiveness (should be ~equal)
            • immediate visual signal if one IP degrades (all
              stations move to the other)
          Read-only. Powered by ``data.by_egress`` produced by the
          backend without touching the recognition path.
      ═════════════════════════════════════════════════════════════ */}
      {data.by_egress && Object.keys(data.by_egress).some(k => k !== '_meta') && (
        <div className="border-t border-purple-300/60 dark:border-purple-800/60" data-testid="egress-breakdown-container">
          {(() => {
            const meta = data.by_egress._meta || {};
            return meta.enabled === false ? (
              <div className="px-3 sm:px-4 py-1.5 text-[11px] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/40 italic" data-testid="egress-multi-disabled-note">
                Multi-IP A/B disabled — the rows below show the
                deterministic assignment that WOULD apply if enabled
                ({meta.this_pod_fleet_id || 'A'} → {meta.this_pod_egress_id || 'direct'}).
              </div>
            ) : (
              <div className="px-3 sm:px-4 py-1.5 text-[11px] text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="egress-multi-enabled-header">
                <span className="font-semibold">Multi-IP A/B ACTIVE</span>
                <span>This pod: fleet <strong>{meta.this_pod_fleet_id}</strong> / egress <strong>{meta.this_pod_egress_id}</strong></span>
                {meta.fallback_stats && meta.fallback_stats.total_fallbacks > 0 && (
                  <span className="text-amber-600 dark:text-amber-400" title={`Last fallback: ${meta.fallback_stats.last_fallback_reason || 'unknown'} from ${meta.fallback_stats.last_fallback_from_egress || '?'}`}>
                    ⚠ {meta.fallback_stats.total_fallbacks} proxy fallback{meta.fallback_stats.total_fallbacks === 1 ? '' : 's'}
                  </span>
                )}
                {meta.config_error && (
                  <span className="text-red-600 dark:text-red-400" title={meta.config_error}>
                    ✖ config error
                  </span>
                )}
              </div>
            );
          })()}
          {(() => {
            // v2026-09-05 — Iterate ALL egress buckets returned by
            // the backend (data.by_egress keys except _meta). Backend
            // is authoritative: it pre-populates every egress in the
            // router pool, so 11-egress mode yields 11 rows.
            const egressKeys = Object.keys(data.by_egress || {}).filter(k => k !== '_meta');
            // Build label map from backend meta.egress_labels when
            // available (includes physical IPs). Falls back to
            // legacy hardcoded labels for direct + hetzner1.
            const labelMap = {};
            const rankMap = {};
            const meta = data.by_egress._meta || {};
            (meta.egress_labels || []).forEach((row) => {
              labelMap[row.egress_id] = row;
              rankMap[row.egress_id] = row.rank;
            });
            const labelFor = (eid, i) => {
              const info = labelMap[eid];
              if (info) {
                if (info.egress_id === 'direct') {
                  return `${info.rank} / direct (native egress)`;
                }
                const ip = info.physical_ip && info.physical_ip !== 'pod-native'
                  ? ` (${info.physical_ip})` : '';
                return `${info.rank} / ${info.name}${ip}`;
              }
              // Legacy fallback labels (pre-11-egress backend).
              if (eid === 'direct') return 'IP-A / direct (native egress)';
              // 2026-02-28 (Phase 3B-DIAG): the legacy hetzner1 label
              // is now suppressed when it is not part of the active
              // pool (11-egress mode). The backend
              // pipeline_diagnostic_extensions._physical_ip_for_egress
              // already masks the IP; we return null here so the row
              // is skipped downstream.
              if (eid === 'hetzner1') return null;
              return `IP-${String(i + 1).padStart(2, '0')} / ${eid}`;
            };
            // Order: direct first, then by rank asc (oxy01..oxy10),
            // then any legacy entries (hetzner1 etc.) last.
            const sortedKeys = egressKeys.slice().sort((a, b) => {
              if (a === 'direct') return -1;
              if (b === 'direct') return 1;
              const ra = rankMap[a] || 'IP-99';
              const rb = rankMap[b] || 'IP-99';
              return ra.localeCompare(rb);
            });
            return sortedKeys.map((egressKey, i) => {
            const bucket = data.by_egress[egressKey];
            if (!bucket) return null;
            const label = labelFor(egressKey, i);
            // 2026-02-28 (Phase 3B-DIAG): labelFor may return null
            // to mask legacy hetzner1 rows when not in active pool.
            if (label === null) return null;
            const rowExpanded = pipelineExpanded[`egress-${egressKey}`];
            const filteredStations = (stations || []).filter(
              (s) => (bucket.station_ids || []).includes(String(s.id)),
            );
            const toggleRow = () => setPipelineExpanded((p) => ({
              ...p, [`egress-${egressKey}`]: !p[`egress-${egressKey}`],
            }));
            return (
              <div key={`egress-${egressKey}`} data-testid={`egress-row-${egressKey}`}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={toggleRow}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleRow();
                    }
                  }}
                  className="w-full flex items-center justify-between gap-2 px-3 sm:px-4 py-2.5 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors cursor-pointer border-t border-gray-200 dark:border-gray-700"
                  data-testid={`egress-row-toggle-${egressKey}`}
                  aria-expanded={rowExpanded}
                >
                  <div className="flex items-center gap-x-3 gap-y-1.5 sm:gap-4 text-sm flex-wrap min-w-0">
                    <div className="flex items-center gap-1.5 font-medium text-gray-700 dark:text-gray-300">
                      <Activity className="w-4 h-4 text-purple-500" />
                      <span>{label}</span>
                      <span
                        className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 ml-1"
                        data-testid={`egress-active-count-${egressKey}`}
                        title="Ενεργοί σταθμοί σε αυτή την egress IP (sticky-hash assignment)"
                      >
                        {bucket.active || 0} active
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                        <HealthDot status="green" /> {bucket.green || 0}
                      </span>
                      <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                        <HealthDot status="yellow" /> {bucket.yellow || 0}
                      </span>
                      <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                        <HealthDot status="red" /> {bucket.red || 0}
                      </span>
                      {(bucket.stuck || 0) > 0 && (
                        <span className="flex items-center gap-1.5 text-cyan-600 dark:text-cyan-400">
                          <HealthDot status="stuck" /> {bucket.stuck}
                        </span>
                      )}
                      {(bucket.off_zone || 0) > 0 && (
                        <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                          <HealthDot status="off_zone" /> {bucket.off_zone}
                        </span>
                      )}
                      {(bucket.inactive || 0) > 0 && (
                        <span className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                          <HealthDot status="inactive" /> {bucket.inactive}
                        </span>
                      )}
                    </div>
                    <span className="text-gray-500 dark:text-gray-400">|</span>
                    <div className="flex items-center gap-2 text-xs flex-wrap" title="Listener-tracking health per egress IP">
                      <Users className="w-3.5 h-3.5 text-purple-500" />
                      <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                        <ListenerDot status="fresh" /> {bucket.listener_fresh || 0}
                      </span>
                      <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                        <ListenerDot status="ok" /> {bucket.listener_ok || 0}
                      </span>
                      {(bucket.listener_warning || 0) > 0 && (
                        <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                          <ListenerDot status="warning" /> {bucket.listener_warning}
                        </span>
                      )}
                      {(bucket.listener_auth || 0) > 0 && (
                        <span className="flex items-center gap-1 text-fuchsia-600 dark:text-fuchsia-400">
                          <ListenerDot status="auth" /> {bucket.listener_auth}
                        </span>
                      )}
                      {(bucket.listener_stale || 0) > 0 && (
                        <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                          <ListenerDot status="stale" /> {bucket.listener_stale}
                        </span>
                      )}
                      {(bucket.listener_no_endpoint || 0) > 0 && (
                        <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                          <ListenerDot status="no_endpoint" /> {bucket.listener_no_endpoint}
                        </span>
                      )}
                    </div>
                    <span className="text-gray-500 dark:text-gray-400">|</span>
                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400" data-testid={`egress-songs-1h-${egressKey}`}>
                      <Music className="w-3.5 h-3.5" /> {bucket.total_songs_1h || 0} <span className="text-xs">τελ. 60΄</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400" data-testid={`egress-listeners-${egressKey}`}>
                      <Users className="w-3.5 h-3.5" /> {(bucket.total_listeners || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400 flex-shrink-0">
                    {rowExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>
                {rowExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2 bg-purple-50/30 dark:bg-purple-900/10" data-testid={`egress-grid-${egressKey}`}>
                    {filteredStations.length === 0 ? (
                      <div className="text-xs text-gray-500 dark:text-gray-400 italic py-2">
                        Κανένας σταθμός δεν έχει ανατεθεί σε αυτή την egress IP.
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                        {filteredStations.map((s) => (
                          <button
                            key={`eg-${egressKey}-${s.id}`}
                            onClick={() => onStationClick?.(s.id)}
                            className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-xs transition-colors text-left w-full hover:ring-1 hover:ring-purple-400/50 cursor-pointer ${cellStyle(s.health)}`}
                            title={`${s.name}\nEgress: ${egressKey}\nSongs/hr: ${s.songs_1h} | Listeners: ${s.listeners}`}
                            data-testid={`egress-${egressKey}-station-${s.id}`}
                          >
                            <HealthDot status={s.health} />
                            <span className="truncate">{s.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
            });
          })()}
        </div>
      )}
    </div>
  );
};

export default StationHealthDashboard;
